import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  LayoutDashboard, User, Shield, Bell, SlidersHorizontal, Lock, CreditCard, Gauge,
  Receipt, FileText, FolderKanban, LifeBuoy, FileSignature, KeyRound, Gift, Scale,
} from "lucide-react";
import { hasCrmAccess } from "@wa/core/crm-access";
import { logout, type AccountUser } from "../lib/auth";
import { portal, type PortalSummary } from "../lib/portal";
import { useCredit } from "../lib/credit";
import { isClientPortalHost } from "../lib/brand";
import { keepPreview } from "../lib/previewMode";
import type { PortalModule } from "@wa/core/portal-modules";
import { InviteModal } from "./InviteModal";
import { SectionView } from "./SectionView";
import { DocSkeleton } from "./ui";
import { HubShell, PortalShell, type NavBadges } from "./shell";
import { useT } from "../i18n/I18nProvider";
import { resolveActiveTab, visibleAccountTabs, type TabKey } from "./tabs";
import { referral } from "../lib/referral";

const ACCOUNT_TABS: { key: TabKey; icon: typeof User }[] = [
  { key: "overview", icon: LayoutDashboard },
  { key: "profile", icon: User },
  { key: "security", icon: Shield },
  { key: "notifications", icon: Bell },
  { key: "preferences", icon: SlidersHorizontal },
  { key: "privacy", icon: Lock },
  { key: "billing", icon: CreditCard },
  { key: "usage", icon: Gauge },
  // "Invite friends" is shown ONLY while the referral programme is on (see referralEnabled).
  { key: "referral", icon: Gift },
  { key: "apikeys", icon: KeyRound },
];

/**
 * Client portal tabs - shown ONLY when the user is linked to a CRM client AND
 * that contact was granted the module behind the tab.
 *
 * `module` is written out rather than inferred from `key`, even though the four
 * pairs happen to be spelled the same today. They are two vocabularies that
 * merely overlap: `TabKey` is this shell's list of screens and `PortalModule`
 * is the server's list of permissions, and the day one of them gains an entry
 * the other does not have, an implicit `key as PortalModule` would compile
 * cleanly and gate the wrong thing. Estimates is already the asymmetric case in
 * the other direction: a module with no tab of its own, drawn as a card inside
 * Invoices.
 */
const PORTAL_TABS: { key: TabKey; icon: typeof User; module: PortalModule }[] = [
  { key: "invoices", icon: Receipt, module: "invoices" },
  // A door of its own, and it used to have none. Estimates were a card at the
  // bottom of the invoices tab, so a contact granted quotes and NOT invoices
  // got a module the API served and the shell drew no way into.
  { key: "estimates", icon: FileText, module: "estimates" },
  // The account statement. Its own door for the same reason estimates got one:
  // the module exists on the server, so a contact granted it and nothing else
  // would otherwise hold a permission with no way into it.
  { key: "statement", icon: Scale, module: "statement" },
  { key: "projects", icon: FolderKanban, module: "projects" },
  { key: "support", icon: LifeBuoy, module: "support" },
  { key: "contracts", icon: FileSignature, module: "contracts" },
];

const PORTAL_KEYS = new Set<TabKey>(PORTAL_TABS.map((t) => t.key));

/** Every account tab, in menu order. Handed to the rules in `tabs.ts` so that
 *  the menu and the `?tab=` guard read one list rather than two. */
const ACCOUNT_TAB_KEYS = ACCOUNT_TABS.map((t) => t.key);

/** The account hub links to web_admin (:3212), which holds BOTH the page editor
 *  AND the CMS management centre. The label depends on the role:
 *   - platform admin (host superadmin / `isHost` membership) -> "Open CMS"
 *     (their workplace IS the CMS backend, not the site editor);
 *   - regular client (manage permission on their NON-host site) -> "Open editor"
 *     (a quick jump into editing their site);
 *   - otherwise (no manageable site) -> no link is shown.
 *  The platform admin is checked first (they may also own a personal site). */
function resolveAdminLink(user: AccountUser): "editor" | "cms" | null {
  // The portal is not a way into Husly's CMS, whoever is holding the account.
  if (isClientPortalHost()) return null;
  const isPlatformAdmin = user.isSuperadmin || user.memberships.some((m) => m.isHost);
  if (isPlatformAdmin) return "cms";
  const isClient = user.memberships.some(
    (m) => !m.isHost && (m.permissions.includes("*") || m.permissions.some((p) => p.startsWith("manage:"))),
  );
  return isClient ? "editor" : null;
}

export function AccountLayout({
  user,
  preview = false,
  onLogout,
  onUserChange,
}: {
  /**
   * Null ONLY in preview mode, where the screen is not about the signed-in
   * person at all - it renders before `/me` settles and never shows their name.
   * Every ordinary path still passes a real user.
   */
  user: AccountUser | null;
  /**
   * An operator looking at this portal as the customer would see it.
   *
   * It changes the SHELL and nothing else: the customer's records are the
   * customer's records either way, because the API decides that from the token.
   * What this flag removes is everything that belongs to the signed-in person -
   * their account tabs, their credit, their sign-out - and everything that would
   * let a preview be mistaken for the real thing.
   */
  preview?: boolean;
  onLogout: () => void;
  onUserChange: (u: AccountUser) => void;
}) {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [summary, setSummary] = useState<PortalSummary | null>(null);
  // Whether the summary has ANSWERED, either way. `summary === null` alone
  // cannot say: it is also what a failed read leaves behind.
  const [summaryReady, setSummaryReady] = useState(false);
  // Referral programme - one probe of `GET /api/account/referral`; 404 = off -> the tab hides.
  const [referralEnabled, setReferralEnabled] = useState(false);
  // The "Get free credit" invite modal (quick sharing with a QR code and a link).
  const [inviteOpen, setInviteOpen] = useState(false);
  // Account credit. `null` = the module is off or this user has no account -> no
  // wallet is shown anywhere in the hub. A zero balance IS shown (see lib/credit).
  const { credit: rawCredit } = useCredit();
  useEffect(() => {
    void portal
      .summary()
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setSummaryReady(true));
    void referral.summary().then(() => setReferralEnabled(true)).catch(() => setReferralEnabled(false));
  }, []);

  // The tabs this contact may actually open. An empty list on a LINKED user is
  // a real state, not a loading artefact: the operator granted portal access and
  // then closed every module. The person still gets their profile, security and
  // privacy tabs - which is the whole of what they are entitled to here.
  const portalTabs = PORTAL_TABS.filter((tab) => summary?.modules?.includes(tab.module));
  // The operator's client portal, not Husly's own hub (see lib/brand.ts). Read
  // once here so every gate below asks the same question the same way.
  const portalBrand = isClientPortalHost();
  // Who is looking, in the three words the rules in `tabs.ts` need. Built once
  // so the menu below and the `?tab=` guard cannot drift apart.
  const audience = { preview, portalBrand, referralEnabled };
  // The account tabs to draw. A preview draws the client's own set (the portal
  // one), because the menu is part of what "view as customer" promises.
  const accountTabs = visibleAccountTabs(ACCOUNT_TAB_KEYS, audience);
  // The tab that renders. A `?tab=` naming something this visit may not open
  // (a module the contact was not granted, a Husly screen on a portal) falls
  // back rather than opening an empty or foreign view; the deep link stays in
  // the URL, so it activates as soon as the summary or the referral probe lands.
  const active = resolveActiveTab({
    ...audience,
    requested: params.get("tab"),
    accountTabs: ACCOUNT_TAB_KEYS,
    openPortalTabs: portalTabs.map((tab) => tab.key),
  });
  // In a preview an account tab draws a notice instead of its screen: those
  // screens read the signed-in OPERATOR's account, which is the one thing the
  // token says nothing about. See PreviewAccountNotice for the full reason.
  //
  // The overview is the exception. On a client-facing shell it is the portal's
  // front page, which reads nothing but the `portal.*` lists - and those carry
  // the preview token, so what it shows is the client's page, not the
  // operator's. It is also the first thing a client sees, which makes it the
  // screen an operator most needs to be able to check.
  const previewAccountTab = preview && !PORTAL_KEYS.has(active) && active !== "overview";
  // The operator's client portal, or a staff preview of it. A preview counts
  // whatever the host says, for the reason `visibleAccountTabs` gives: the link
  // is minted onto the portal host in production, but a developer opens it on
  // localhost.
  const clientFacing = portalBrand || preview;

  // The Husly credit wallet is Husly's, so it disappears with the rest of the
  // Husly surface. `null` is the same value the hook returns when the module is
  // off, so every reader below already handles it.
  const credit = portalBrand || preview ? null : rawCredit;
  // No way into the CMS from a preview, and no account to resolve one from.
  const adminLink = user && !preview ? resolveAdminLink(user) : null;
  // The staff CRM, for somebody the CRM would let in. Unlike the CMS link above
  // this one DOES belong on the client portal: the portal is WebAdmin-branded
  // and shares webadmin.ee with the CRM, so for an operator standing there the
  // jump is seamless. A portal client fails `hasCrmAccess` and never sees it.
  // Hidden in a preview for the same reason the CMS link is: the token names a
  // client, and the menu has to be the one that client would see.
  const crmLink = Boolean(user && !preview && hasCrmAccess(user));
  function go(tab: TabKey) {
    // `keepPreview` and not a bare object: `setParams` REPLACES the query string,
    // so without it the first tab click would drop the preview token and the
    // portal would empty itself mid-preview - which looks exactly like a customer
    // with no records rather than like a link that expired. On an ordinary visit
    // it returns the object unchanged.
    setParams(keepPreview({ tab }), { replace: false });
  }

  async function doLogout() {
    await logout().catch(() => {});
    onLogout();
  }

  const section = (
    <SectionView
      active={active}
      user={user}
      preview={preview}
      clientFacing={clientFacing}
      previewAccountTab={previewAccountTab}
      summary={summary}
      credit={credit}
      referralEnabled={referralEnabled}
      onInvite={() => setInviteOpen(true)}
      onUserChange={onUserChange}
      onLogout={onLogout}
    />
  );

  // ── The client portal ────────────────────────────────────────────────────
  // One navigation: the front page and the services this contact was granted.
  // The person's own screens (profile, security, notifications, preferences,
  // privacy) are in the avatar menu, so none of them is in this list.
  if (clientFacing) {
    // What is waiting behind a destination, from the numbers the summary
    // already carries - no list is fetched to draw a badge. Red only for money
    // that is late; a quote waiting for an answer is the brand colour.
    const stats = summary?.linked ? summary.stats : null;
    const badges: NavBadges = {};
    if (stats && stats.overdueInvoices > 0) {
      badges.invoices = { count: stats.overdueInvoices, tone: "destructive", label: t("portal.overview.overdue") };
    }
    if (stats && stats.pendingEstimates > 0) {
      badges.estimates = { count: stats.pendingEstimates, tone: "default", label: t("portal.overview.pendingEstimates") };
    }
    // A link straight to a service ("your invoice is ready", the card payment's
    // return address) arrives before the summary has said which services this
    // contact may open. Until it does, `active` falls back to the front page -
    // and drawing that would greet the reader, start to load, and then slide
    // away to the page they asked for. A placeholder under the requested tab
    // holds the place instead; if the summary then says the tab is closed, the
    // fallback happens once, on real information.
    const requested = params.get("tab") as TabKey | null;
    const awaiting = !summaryReady && requested !== null && PORTAL_KEYS.has(requested);
    return (
      <PortalShell
        user={user}
        preview={preview}
        summary={summary}
        crmLink={crmLink}
        active={awaiting && requested ? requested : active}
        nav={[{ key: "overview", icon: LayoutDashboard }, ...portalTabs]}
        badges={badges}
        menuTabs={accountTabs.filter((key) => key !== "overview")}
        onGo={go}
        onLogout={doLogout}
      >
        {awaiting ? <DocSkeleton label={t("common.loading")} /> : section}
      </PortalShell>
    );
  }

  // ── Husly's own hub ──────────────────────────────────────────────────────
  // Never reached without a signed-in person: `main.tsx` renders the login page
  // for a visitor with no session, and the only path that passes a null user is
  // a preview, which took the branch above.
  if (!user) return null;

  return (
    <>
      <HubShell
        user={user}
        adminLink={adminLink}
        crmLink={crmLink}
        active={active}
        portalTabs={portalTabs}
        accountTabs={ACCOUNT_TABS.filter(({ key }) => accountTabs.includes(key))}
        referralEnabled={referralEnabled}
        onGo={go}
        onInvite={() => setInviteOpen(true)}
        onLogout={doLogout}
      >
        {section}
      </HubShell>

      {/* Invite modal - OUTSIDE the shell's AnimatePresence (the portal child would
          otherwise drop out on the exit animation, see the AnimatePresence-portal
          gotcha). */}
      <InviteModal open={inviteOpen} onOpenChange={setInviteOpen} onOpenDetails={() => go("referral")} />
    </>
  );
}
